import type { NavigationEventEmitter } from "./navigationEvents";
import type { ExcelEventSource } from "./excelEvents";

type PaneStatus = "open" | "closed";

interface NavigationState {
  workbookId: string;
  workbookName: string;
  worksheetId: string;
  worksheetName: string;
  rangeAddress: string;
  paneStatus: PaneStatus;
}

const OFFICE_SCHEME = "office://";
const OFFICE_HOST = "excel";

function sanitizeUrlSegment(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return fallback;
  }
  return encodeURIComponent(trimmed);
}

export class NavigationTracker {
  private currentState: NavigationState = {
    workbookId: 'unknown',
    workbookName: 'unknown',
    worksheetId: 'unknown',
    worksheetName: 'unknown',
    rangeAddress: 'unknown',
    paneStatus: 'open',
  };
  private currentUrl: string = '';

  constructor(
    private readonly appEvents: ExcelEventSource,
    private readonly navEmitter: NavigationEventEmitter)
  {
    this.appEvents.events.taskpaneOpened.on(() => this.updateNavState({paneStatus: "open"}));
    this.appEvents.events.taskpaneClosed.on(() => this.updateNavState({paneStatus: "closed"}));

    this.appEvents.events.workbookOpened.on((payload) => this.updateNavState({workbookId: payload.workbook.id, workbookName: payload.workbook.name}));
    this.appEvents.events.worksheetActivated.on((payload) => {
      this.updateNavState({worksheetId: payload.worksheet.id, worksheetName: payload.worksheet.name})
    });
    this.appEvents.events.worksheetNameChanged.on((payload) => {
      if (this.currentState.worksheetId !== payload.worksheetId) return;
      this.updateNavState({worksheetId: payload.worksheetId, worksheetName: payload.to});
    });
    this.appEvents.events.selectionChanged.on((payload) => this.updateNavState({... payload}));
  }

  private updateNavState(stateChange: Partial<NavigationState>): void {
    this.currentState = {
      ... this.currentState,
      ... stateChange
    };
    const newUrl = this.buildUrl(this.currentState);
    const urlChanged = newUrl !== this.currentUrl;
    if (!urlChanged) return;

    this.navEmitter.emit("pageview", {
      $current_url: newUrl,
      $referrer: this.currentUrl,
    });
    this.currentUrl = newUrl;
  }

  private buildUrl(state: NavigationState): string {
    const workbookSegment = sanitizeUrlSegment(state.workbookId, "workbook");
    const worksheetSegment = sanitizeUrlSegment(state.worksheetId, "worksheet");

    const queryParams = new URLSearchParams();
    queryParams.set("range", state.rangeAddress);
    queryParams.set("workbookName", state.workbookName);
    queryParams.set("sheetName", state.worksheetName);

    const query = queryParams.toString();
    return `${OFFICE_SCHEME}${OFFICE_HOST}/${workbookSegment}/${worksheetSegment}?${query}#taskPane_${state.paneStatus}`;
  }
}